import {
  ComponentRef,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  delay,
  finalize,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import {
  IGenericModalConfirm,
  ModalSizeEnum,
} from 'src/app/model/generic-modal-confirm.model';
import { GenericModalConfirmComponent } from '../../components/message-components/modal/generic-modal-confirm/generic-modal-confirm.component';
import { randomID } from '../../utils/utils';
import { MessageManagerMessageTypeEnum } from '../messages/message.manager.service';
import { ModalArgument } from '@model/modal-argument.model';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class ModalManagerService {
  genericMessageRefContainer!: ViewContainerRef;

  notifyClosingModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  modalInstanced: GenericModalConfirmComponent[] = [];

  openModal(el: GenericModalConfirmComponent) {
    el.showModal();
    this.modalInstanced.push(el);
  }

  hideModal(el: GenericModalConfirmComponent) {
    setTimeout(() => {
      el.closeModal();
      this.modalInstanced.pop();
    }, 0);
  }

  createOpenGenericModalV2(
    componentType: Type<any>,
    viewRef: ViewContainerRef | undefined = undefined,
    args?: ModalArgument[]
  ) {
    const component = viewRef
      ? viewRef.createComponent(componentType)
      : this.genericMessageRefContainer.createComponent(componentType);

    if (args) {
      for(let arg of args){
        component.instance[arg.name] = _.cloneDeep(arg.value);
      }
    }

    return of(component.instance).pipe(
      delay(0),
      switchMap((instance) => {
        const modalInstance =
          instance.getGenericModalConfirmInstance() as GenericModalConfirmComponent;
        this.openModal(modalInstance);
        return modalInstance.closeEvent.pipe(
          map((closePressed) => {
            let modalData: any;
            if('getData' in instance){
              modalData = instance.getData();
            }
            this.modalInstanced.pop();
            component.destroy();
            return {closePressed:closePressed, modalData: modalData}
          })
        );
      })
    );
  }

  createOpenGenericModal(
    componentType: Type<any>,
    viewRef: ViewContainerRef | undefined = undefined,
    ...args: any[]
  ) {
    if (this.modalInstanced.length == 0) {
      const component = viewRef
        ? viewRef.createComponent(componentType)
        : this.genericMessageRefContainer.createComponent(componentType);
      if (args) {
        component.instance.inputs = args;
      }
      return new Promise((resolve) => setTimeout(resolve, 0)).then(() => {
        setTimeout(() => {
          this.openModal(
            component.instance.getGenericModalConfirmInstance() as GenericModalConfirmComponent
          );
        }, 0);
        const sub: Subscription = component.instance
          .getGenericModalConfirmInstance()
          .closeEvent.pipe(finalize(() => sub.unsubscribe()))
          .subscribe((closed: boolean) => {
  /*           if (closed) { */
              component.destroy();
              this.modalInstanced.pop();
/*             } */
          });
        return component.instance;
      });
    }
    return null;
  }

  createOpenTextModal(
    title: string,
    message: string,
    type: MessageManagerMessageTypeEnum = MessageManagerMessageTypeEnum.INFO,
    viewRef: ViewContainerRef | undefined = undefined,
    seeMultiModal: boolean = false,
    needIcon: boolean = true,
    size: ModalSizeEnum = ModalSizeEnum.GRANDE,
    scrollable: boolean = false,
    staticBackdrop: boolean = false,
    filterDuplicates: boolean = false
  ) {
    if ((this.modalInstanced.length == 0 && !seeMultiModal) || seeMultiModal) {
      const modalConfig: IGenericModalConfirm = {
        id: randomID(),
        title: title,
        message: message,
        type: type,
        needIcon: needIcon,
        size: size,
        scrollable: scrollable,
        staticBackdrop: staticBackdrop,
        crossDismissable: true,
      };
      if (
        !filterDuplicates ||
        (filterDuplicates &&
          !this.modalInstanced.some(
            (x) => x.modalConfig.message == modalConfig.message
          ))
      ) {
        const instanceModal: ComponentRef<GenericModalConfirmComponent> =
          viewRef
            ? viewRef.createComponent(GenericModalConfirmComponent)
            : this.genericMessageRefContainer.createComponent(
                GenericModalConfirmComponent
              );
        instanceModal.instance.modalConfig = modalConfig;
        setTimeout(() => {
          setTimeout(() => {
            this.openModal(instanceModal.instance);
          });
          if (seeMultiModal) {
            this.notifyClosingModal.next(true);
          }
          const sub: Subscription = instanceModal.instance.closeEvent
            .pipe(finalize(() => sub.unsubscribe()))
            .subscribe((closed: boolean) => {
  /*             if (closed) { */
                instanceModal.destroy();
                this.modalInstanced.pop();
                if (!seeMultiModal) {
                  this.notifyClosingModal.next(true);
                }
/*               } */
            });
        }, 0);
      } else {
        setTimeout(() => {
          this.notifyClosingModal.next(true);
        }, 0);
      }
    }
  }

  closeAllModals() {
    this.modalInstanced.forEach((x) => x.closeModal());
  }
}
